// SuperCompanyDetailPage — /super/companies/:enc_id.
//
// Two-column layout (left = profile + tier/status auto-save dropdowns,
// right = usage stats). Impersonate button at the top with the same
// confirmation modal flow used on the list page.
//
// Below the two columns: a Modules section. Each row is one
// `company_modules` subscription (collections / skip / weighbridge). Tier
// dropdown, mobile-dispatch toggle, deactivate / reactivate button, plus
// a modal for adding a new module.

import { useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, HardDrive, ShieldCheck, UserCog } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Spinner } from '../../components/ui/Spinner/Spinner';
import { Modal } from '../../components/ui/Modal/Modal';
import { Select } from '../../components/form/Select';
import { Checkbox } from '../../components/form/Checkbox';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import {
  MODULE_KEYS,
  MODULE_TIER_KEYS,
  extractDowngradeViolations,
  useAddCompanyModule,
  useCompanyDetail,
  useCompanyModules,
  useImpersonateCompany,
  useUpdateCompany,
  useUpdateCompanyModule,
  type CompanyModuleItem,
  type ModuleKey,
  type ModuleTierKey,
  type TierDowngradeViolation,
} from './hooks/useSuper';
import styles from './SuperCompanyDetailPage.module.css';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

// Must mirror server super.types.ts TIER_KEYS. Any value not in this list
// fails server-side tier lookup and now returns 400 (see fix in
// super.queries.ts:updateCompanyTier). Keep the create-page list and this
// list in sync.
const TIER_OPTIONS = [
  { value: 'starter', label: 'Starter' },
  { value: 'professional', label: 'Professional' },
  { value: 'enterprise', label: 'Enterprise' },
];

const MODULE_TIER_OPTIONS = MODULE_TIER_KEYS.map((t) => ({
  value: t,
  label: t.charAt(0).toUpperCase() + t.slice(1),
}));

const MODULE_OPTIONS = MODULE_KEYS.map((m) => ({
  value: m,
  label: m.charAt(0).toUpperCase() + m.slice(1),
}));

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-GB').format(n);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB');
}

function moduleSupportsMobile(m: ModuleKey): boolean {
  return m !== 'weighbridge';
}

export function SuperCompanyDetailPage(): JSX.Element {
  const { enc_id: encId = '' } = useParams<{ enc_id: string }>();
  const navigate = useNavigate();
  const detail = useCompanyDetail(encId);
  const update = useUpdateCompany();
  const impersonate = useImpersonateCompany();
  const modules = useCompanyModules(encId);
  const addModule = useAddCompanyModule();
  const updateModule = useUpdateCompanyModule();

  const [confirmImpersonate, setConfirmImpersonate] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<ModuleKey | null>(
    null,
  );
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<{
    module: ModuleKey | '';
    tier: ModuleTierKey;
    mobile: boolean;
  }>({ module: '', tier: 'starter', mobile: true });
  const [violations, setViolations] = useState<
    Record<string, TierDowngradeViolation[]>
  >({});

  const c = detail.data;

  const usedModules = useMemo(
    () => new Set((modules.data ?? []).map((m) => m.module)),
    [modules.data],
  );
  const availableModuleOptions = useMemo(
    () => MODULE_OPTIONS.filter((opt) => !usedModules.has(opt.value)),
    [usedModules],
  );

  if (detail.isLoading || !c) {
    return (
      <div className={styles.page}>
        <PageHeader title="Company" />
        <Card>
          <div className={styles.empty}>
            <Spinner />
          </div>
        </Card>
      </div>
    );
  }

  function handleStatusChange(value: string) {
    if (!encId) return;
    update.mutate({
      encId,
      body: { status: value as 'active' | 'inactive' | 'suspended' },
    });
  }

  function handleTierChange(value: string) {
    if (!encId) return;
    update.mutate({ encId, body: { tier: value } });
  }

  function clearViolations(module: ModuleKey) {
    setViolations((prev) => {
      if (prev[module] === undefined) return prev;
      const next = { ...prev };
      delete next[module];
      return next;
    });
  }

  async function patchModule(module: ModuleKey, body: {
    tier?: ModuleTierKey;
    is_active?: boolean;
    mobile_dispatch_enabled?: boolean;
  }) {
    if (!encId) return;
    try {
      await updateModule.mutateAsync({ encId, module, body });
      clearViolations(module);
    } catch (err) {
      const v = extractDowngradeViolations(err);
      if (v !== null) {
        setViolations((prev) => ({ ...prev, [module]: v }));
      }
    }
  }

  async function handleAddSubmit() {
    if (!encId || addForm.module === '') return;
    const body: {
      module: ModuleKey;
      tier: ModuleTierKey;
      mobile_dispatch_enabled?: boolean;
    } = { module: addForm.module, tier: addForm.tier };
    if (moduleSupportsMobile(addForm.module)) {
      body.mobile_dispatch_enabled = addForm.mobile;
    }
    await addModule.mutateAsync({ encId, body });
    setAddOpen(false);
    setAddForm({ module: '', tier: 'starter', mobile: true });
  }

  function openAddModal() {
    setAddForm({
      module: '',
      tier: 'starter',
      mobile: true,
    });
    setAddOpen(true);
  }

  function renderModuleRow(m: CompanyModuleItem) {
    const supportsMobile = moduleSupportsMobile(m.module);
    const rowClass = m.is_active
      ? styles.moduleRow
      : `${styles.moduleRow} ${styles.moduleRowInactive ?? ''}`;
    const rowViolations = violations[m.module];
    return (
      <div key={m.module} className={rowClass} data-module={m.module}>
        <div className={styles.moduleHeader}>
          <span className={styles.moduleName}>{m.module}</span>
          <span
            className={`${styles.statusBadge} ${
              m.is_active
                ? styles.statusBadgeActive ?? ''
                : styles.statusBadgeInactive ?? ''
            }`}
          >
            {m.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div className={styles.moduleControls}>
          <Select
            label="Tier"
            value={m.tier}
            onChange={(e) =>
              void patchModule(m.module, {
                tier: e.target.value as ModuleTierKey,
              })
            }
            options={MODULE_TIER_OPTIONS}
            disabled={updateModule.isPending || !m.is_active}
          />
          {supportsMobile ? (
            <div className={styles.toggleField}>
              <span className={styles.toggleLabel}>Mobile dispatch</span>
              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={m.mobile_dispatch_enabled}
                  disabled={updateModule.isPending || !m.is_active}
                  aria-label={`Mobile dispatch for ${m.module}`}
                  onChange={(e) =>
                    void patchModule(m.module, {
                      mobile_dispatch_enabled: e.target.checked,
                    })
                  }
                />
                <span>{m.mobile_dispatch_enabled ? 'On' : 'Off'}</span>
              </label>
            </div>
          ) : null}
        </div>
        {rowViolations !== undefined && rowViolations.length > 0 ? (
          <div className={styles.violationsCard} role="alert">
            <strong>Tier downgrade blocked.</strong> Current usage exceeds the
            target tier limits:
            <ul className={styles.violations}>
              {rowViolations.map((v) => (
                <li key={v.field}>
                  {v.field}: {formatNumber(v.current)} (limit{' '}
                  {formatNumber(v.limit)})
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <div className={styles.moduleActions}>
          {m.is_active ? (
            <Button
              variant="danger"
              size="sm"
              onClick={() => setDeactivateTarget(m.module)}
              disabled={updateModule.isPending}
            >
              Deactivate
            </Button>
          ) : (
            <Button
              variant="primary"
              size="sm"
              onClick={() =>
                void patchModule(m.module, { is_active: true })
              }
              disabled={updateModule.isPending}
            >
              Reactivate
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={c.name}
        subtitle={c.subdomain ? `${c.subdomain}.tricivo` : 'No subdomain'}
        actions={
          <div className={styles.headerActions}>
            <Button
              variant="ghost"
              icon={ArrowLeft}
              onClick={() => navigate('/super/companies')}
            >
              Back
            </Button>
            <Button
              variant="primary"
              icon={UserCog}
              onClick={() => setConfirmImpersonate(true)}
              disabled={c.status !== 'active'}
            >
              Impersonate
            </Button>
          </div>
        }
      />

      <div className={styles.grid}>
        <Card title="Profile">
          <dl className={styles.dl}>
            <dt>UUID</dt>
            <dd className={styles.mono}>{c.uuid}</dd>
            <dt>Email</dt>
            <dd>{c.email ?? '—'}</dd>
            <dt>Phone</dt>
            <dd>{c.phone ?? '—'}</dd>
            <dt>Address</dt>
            <dd>{c.address ?? '—'}</dd>
            <dt>Postcode</dt>
            <dd>{c.postcode ?? '—'}</dd>
            <dt>Created</dt>
            <dd>{formatDate(c.created_at)}</dd>
          </dl>

          <div className={styles.controlsRow}>
            <Select
              label="Status"
              value={c.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              options={STATUS_OPTIONS}
              disabled={update.isPending}
              helper="Auto-saves on change."
            />
            <Select
              label="Tier"
              value={c.tier ?? ''}
              onChange={(e) => handleTierChange(e.target.value)}
              options={TIER_OPTIONS}
              disabled={update.isPending}
              helper="Auto-saves on change."
            />
          </div>
        </Card>

        <Card title="Usage">
          <ul className={styles.statList}>
            <li>
              <span>Staff</span>
              <strong>{formatNumber(c.staff_count)}</strong>
            </li>
            <li>
              <span>Drivers (active / total)</span>
              <strong>
                {formatNumber(c.active_driver_count)} / {formatNumber(c.driver_count)}
              </strong>
            </li>
            <li>
              <span>Bookings (total)</span>
              <strong>{formatNumber(c.booking_count_total)}</strong>
            </li>
            <li>
              <span>Bookings (last 30d)</span>
              <strong>{formatNumber(c.booking_count_30d)}</strong>
            </li>
            <li>
              <span>Receipts (total)</span>
              <strong>{formatNumber(c.receipt_count_total)}</strong>
            </li>
            <li>
              <span>Last booking</span>
              <strong>{formatDate(c.last_booking_at)}</strong>
            </li>
            <li>
              <span>Last login (any staff)</span>
              <strong>{formatDate(c.last_login_at)}</strong>
            </li>
            <li>
              <span>Queue pending</span>
              <strong>{formatNumber(c.message_queue_pending)}</strong>
            </li>
            <li>
              <span>Queue failed</span>
              <strong>{formatNumber(c.message_queue_failed)}</strong>
            </li>
          </ul>
        </Card>
      </div>

      <Card title="Modules">
        {modules.isLoading ? (
          <div className={styles.empty}>
            <Spinner />
          </div>
        ) : (modules.data ?? []).length === 0 ? (
          <div className={styles.moduleEmpty}>
            This company has no module subscriptions yet.
          </div>
        ) : (
          <div className={styles.moduleList}>
            {(modules.data ?? []).map(renderModuleRow)}
          </div>
        )}
        <div className={styles.addRow}>
          <Button
            variant="primary"
            size="sm"
            onClick={openAddModal}
            disabled={availableModuleOptions.length === 0}
          >
            + Add Module
          </Button>
        </div>
      </Card>

      <Card title="Storage">
        <p className={styles.storageDesc}>
          Configure where this company's files (photos, signatures, PDFs) are stored.
        </p>
        <Link to={`/super/companies/${encId}/storage`} className={styles.storageLink}>
          <HardDrive size={16} aria-hidden />
          Manage Storage Configuration
        </Link>
      </Card>

      <ConfirmModal
        open={confirmImpersonate}
        title="Impersonate this company?"
        confirmLabel="Yes, impersonate"
        variant="default"
        loading={impersonate.isPending}
        onConfirm={async () => {
          await impersonate.mutateAsync(encId);
          setConfirmImpersonate(false);
        }}
        onCancel={() => setConfirmImpersonate(false)}
        message={
          <span className={styles.modalMessage}>
            <ShieldCheck size={16} aria-hidden />
            <span>
              You are about to impersonate <strong>{c.name}</strong>. The
              session lasts 15 minutes and every action will be audit-logged.
              Continue?
            </span>
          </span>
        }
      />

      <ConfirmModal
        open={deactivateTarget !== null}
        title="Deactivate this module?"
        confirmLabel="Yes, deactivate"
        variant="danger"
        loading={updateModule.isPending}
        onConfirm={async () => {
          if (deactivateTarget === null) return;
          await patchModule(deactivateTarget, { is_active: false });
          setDeactivateTarget(null);
        }}
        onCancel={() => setDeactivateTarget(null)}
        message={
          <span className={styles.modalMessage}>
            <ShieldCheck size={16} aria-hidden />
            <span>
              Deactivating{' '}
              <strong>
                {deactivateTarget ? deactivateTarget : 'this module'}
              </strong>{' '}
              will prevent this company's staff from accessing it immediately.
              Continue?
            </span>
          </span>
        }
      />

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add module"
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setAddOpen(false)}
              disabled={addModule.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddSubmit}
              loading={addModule.isPending}
              disabled={addForm.module === ''}
            >
              Add Module
            </Button>
          </>
        }
      >
        <div className={styles.addModuleForm}>
          <Select
            label="Module"
            value={addForm.module}
            onChange={(e) =>
              setAddForm((prev) => ({
                ...prev,
                module: e.target.value as ModuleKey | '',
              }))
            }
            options={availableModuleOptions}
            placeholder={
              availableModuleOptions.length === 0
                ? 'No modules available'
                : 'Select a module'
            }
            disabled={availableModuleOptions.length === 0}
          />
          <Select
            label="Tier"
            value={addForm.tier}
            onChange={(e) =>
              setAddForm((prev) => ({
                ...prev,
                tier: e.target.value as ModuleTierKey,
              }))
            }
            options={MODULE_TIER_OPTIONS}
          />
          {addForm.module !== '' &&
          moduleSupportsMobile(addForm.module) ? (
            <Checkbox
              label="Enable mobile dispatch"
              checked={addForm.mobile}
              onChange={(e) =>
                setAddForm((prev) => ({ ...prev, mobile: e.target.checked }))
              }
            />
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
